import { Link } from 'react-router-dom';
import { Crown, Sparkles, Check, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/client/components/ui/dialog';
import { Button } from '@/client/components/ui/button';
import {
  useFeatures,
  TIER_NAMES,
  TIER_PRICING,
  getUpgradeMessage,
  type FeatureFlag,
} from '@/client/hooks/use-features';

/**
 * UpgradeModal - Prompts users to upgrade when they try to access a gated feature
 *
 * Part of TSK-020 Feature Gating Consistency implementation.
 * Provides a clear path from "I want this feature" to "here's how to get it".
 *
 * @example
 * <UpgradeModal
 *   open={showModal}
 *   onOpenChange={setShowModal}
 *   feature="custom_short_codes"
 * />
 */

interface UpgradeModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** The feature being gated */
  feature: FeatureFlag;
  /** Optional custom message to display */
  message?: string;
}

/**
 * Feature benefits by tier for the modal
 */
const TIER_BENEFITS: Record<'plus' | 'pro', string[]> = {
  plus: [
    'Custom vanity URLs (arml.ink/your-brand)',
    'Extended analytics (1 year history)',
    'Conditional form logic',
    'Export analytics as CSV',
    'Remove QR code watermark',
    'Up to 500 links/month',
  ],
  pro: [
    'Everything in Plus, and:',
    'Landing page builder',
    'Team collaboration',
    'API access',
    'Custom social previews',
    'Unlimited links & analytics',
  ],
};

export function UpgradeModal({ open, onOpenChange, feature, message }: UpgradeModalProps) {
  const { getRequiredTier, isBeta } = useFeatures();
  const requiredTier = getRequiredTier(feature);
  const tierName = TIER_NAMES[requiredTier];
  const pricing = TIER_PRICING[requiredTier];
  const upgradeMessage = message || getUpgradeMessage(feature);
  const benefits = TIER_BENEFITS[requiredTier as 'plus' | 'pro'] || TIER_BENEFITS.plus;
  const isFeatureBeta = isBeta(feature);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          {/* Icon */}
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30">
            <Crown className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>

          <DialogTitle className="text-xl">
            Unlock {tierName} Features
            {isFeatureBeta && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded-full">
                <Sparkles className="h-3 w-3" />
                Beta
              </span>
            )}
          </DialogTitle>

          <DialogDescription className="text-base">{upgradeMessage}</DialogDescription>
        </DialogHeader>

        {/* Benefits List */}
        <div className="my-4 space-y-2.5 rounded-xl bg-muted/50 p-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            {tierName} includes:
          </p>
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-2.5">
              <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-2.5 w-2.5 text-primary" />
              </div>
              <span className="text-sm">{benefit}</span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        {pricing.yearly && (
          <div className="text-center pb-2">
            <p className="text-2xl font-bold">
              ${pricing.yearly / 12}
              <span className="text-sm font-normal text-muted-foreground">/month</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Billed annually (${pricing.yearly}/year)
            </p>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button asChild size="lg" className="w-full gap-2">
            <Link to="/settings/billing">
              Upgrade to {tierName}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full text-muted-foreground"
          >
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Standalone hook to control the upgrade modal
 * Useful when you need more control over when the modal opens
 */
export function useUpgradeModal(feature: FeatureFlag) {
  const { hasFeature, getRequiredTier } = useFeatures();

  return {
    hasAccess: hasFeature(feature),
    requiredTier: getRequiredTier(feature),
    tierName: TIER_NAMES[getRequiredTier(feature)],
  };
}
